import type { EntityAction } from "@firecms/core";
import { PersonOffIcon } from "@firecms/ui";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import type { AccountUser } from "../collections/accountUsers";
import { getAdminT } from "../i18n/getAdminT";
import { getAdminUiLocale } from "../i18n/getAdminUiLocale";
import { tableOnlyEntityAction } from "./entityActionDefaults";
import { openConfirmTextAction } from "./openConfirmTextAction";

export function buildRemoveUserFromAccountEntityAction(
  accountId: string
): EntityAction<AccountUser> {
  const t = getAdminT(getAdminUiLocale());

  return {
    key: "remove-user-from-account",
    name: t("removeUserFromAccount.entityAction"),
    icon: <PersonOffIcon />,
    ...tableOnlyEntityAction,
    collapsed: false,
    onClick: ({ entity, context }) => {
      if (!entity) {
        return;
      }

      const userId = entity.values.uid || entity.id;
      const currentUid = context.authController.user?.uid;
      if (currentUid && userId === currentUid) {
        context.snackbarController.open({
          type: "warning",
          message: t("removeUserFromAccount.selfNotAllowed"),
        });
        return;
      }

      openConfirmTextAction(context, {
        key: `remove-user-from-account-${entity.id}`,
        title: t("removeUserFromAccount.title"),
        body: t("removeUserFromAccount.body"),
        onConfirm: async () => {
          try {
            const functions = getFunctions(getApp());
            const callable = httpsCallable(functions, "removeUserFromAccount");
            await callable({ accountId, userId });
            context.snackbarController.open({
              type: "success",
              message: t("removeUserFromAccount.success"),
            });
            return true;
          } catch (error: unknown) {
            const message =
              error instanceof Error
                ? error.message
                : t("common.unknownError");
            context.snackbarController.open({
              type: "error",
              message: t("removeUserFromAccount.error", { message }),
            });
            console.error(error);
            return false;
          }
        },
      });
    },
  };
}
